import { useMutation } from '@apollo/client';

import { TextInputLight } from 'components/site/layouts/DefaultPageLayout/TextInputLight';
import { S_WebButtonSmaller } from 'elements/button/WebButton';
import {
    ChangeUserPassword,
    ChangeUserPasswordVariables
} from 'libs/graphql/mutation/access/__generated__/ChangeUserPassword';
import { CHANGE_USER_PASSWORD_MUTATION } from 'libs/graphql/mutation/access/accessMutation';
import { EShadows } from 'libs/styles/styling';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const ChangePasswordForm = (props: Props): JSX.Element => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newConfirmPassword, setNewConfirmPassword] = useState('');

    const [submit] = useMutation<ChangeUserPassword, ChangeUserPasswordVariables>(
        CHANGE_USER_PASSWORD_MUTATION,
        {
            variables: {
                currentPassword,
                newPassword,
                newConfirmPassword
            },
            onCompleted: () => {
                setCurrentPassword('');
                setNewPassword('');
                setNewConfirmPassword('');
                userMessage(EToastType.SUCCESS, 'Password changed');
            },
            onError: (err) => {
                const r = err.message;
                if (r === 'CURRENT_PASSWORD_NOT_VALID') {
                    userMessage(EToastType.ERROR, 'Current password is not valid');
                } else if (r === 'PASSWORD_NOT_VALID') {
                    userMessage(
                        EToastType.ERROR,
                        'Passwords need to have at least 6 characters and match'
                    );
                } else {
                    userMessage(EToastType.ERROR, 'Error while changing password');
                }
            }
        }
    );

    const canSubmit = currentPassword && newPassword && newConfirmPassword;

    return (
        <S_ChangePasswordForm className={props.className}>
            <form
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        if (canSubmit) {
                            submit();
                        }
                    }
                }}
            >
                <S_TextInputLight
                    value={currentPassword}
                    label="Current password"
                    autoComplete="current-password"
                    autoFocus={true}
                    onChange={(value) => {
                        setCurrentPassword(value);
                    }}
                    type="password"
                />
                <S_TextInputLight
                    value={newPassword}
                    label="New password"
                    autoComplete="new-password"
                    onChange={(value) => {
                        setNewPassword(value);
                    }}
                    type="password"
                />
                <S_TextInputLight
                    value={newConfirmPassword}
                    label="Confirm new password"
                    autoComplete="new-password"
                    onChange={(value) => {
                        setNewConfirmPassword(value);
                    }}
                    type="password"
                />
                <S_Center>
                    <S_WebButton
                        text={'Change password'}
                        type="primary"
                        shadow={EShadows.LIGHT}
                        onClick={() => {
                            submit();
                        }}
                        disabled={!canSubmit}
                    />
                </S_Center>
            </form>
        </S_ChangePasswordForm>
    );
};

const S_ChangePasswordForm = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const S_TextInputLight = styled(TextInputLight)`
    margin-bottom: 20px;
`;

const S_Center = styled.div`
    display: flex;
    justify-content: center;
`;

const S_WebButton = styled(S_WebButtonSmaller)`
    margin: 0 0 15px 0;
`;
